import React, { useState } from 'react';
import { Shield, ChevronRight, Check, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A961';
const ORANGE = '#FF5E00';
const BG = '#050508';

const AcademyApplyComingSoon = () => {
  const [form, setForm] = useState({ parent_name: '', parent_email: '', student_name: '', student_grade: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/academy/apply-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Submit failed');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen text-white pt-24 pb-20 saa-dark-form" style={{ backgroundColor: BG }} data-testid="apply-coming-soon-page">
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(201,169,97,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.06) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
      <div className="relative mx-auto max-w-2xl px-6 lg:px-8">
        <a href="/academy" className="inline-flex items-center gap-2 text-[10px] font-black tracking-[0.28em] uppercase text-gray-500 hover:text-white transition-colors mb-8">
          <ArrowLeft className="h-3 w-3" /> Back to Academy
        </a>

        <div className="inline-flex items-center gap-2 border border-white/10 backdrop-blur-xl px-4 py-2 mb-6">
          <Shield className="w-3.5 h-3.5" style={{ color: GOLD }} />
          <span className="text-[10px] font-black tracking-[0.28em] uppercase" style={{ color: GOLD }}>Enrollment Portal · Opening Soon</span>
        </div>

        <h1 className="text-4xl md:text-5xl font-black uppercase leading-[1.05] tracking-[-0.02em] mb-6">
          Your seat at <br/>
          <span className="text-transparent bg-clip-text" style={{ WebkitBackgroundClip: 'text', backgroundImage: `linear-gradient(135deg, ${ORANGE}, ${GOLD})` }}>
            the Sovereign Academy
          </span> is<br/>almost ready.
        </h1>

        <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-xl">
          The full 15-step enrollment portal — with document uploads, Stripe tuition, and parent onboarding — is being wired up. Drop your info and we&apos;ll email you the moment it goes live.
        </p>

        {submitted ? (
          <div
            className="p-8"
            style={{ backgroundColor: '#0A0A0E', border: `1px solid ${GOLD}40` }}
            data-testid="apply-interest-success"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 mb-4" style={{ backgroundColor: `${GOLD}20`, border: `1px solid ${GOLD}` }}>
              <Check className="h-6 w-6" style={{ color: GOLD }} />
            </div>
            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">You&apos;re on the list.</h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              We&apos;ll email <b className="text-white">{form.parent_email}</b> as soon as the enrollment portal opens. In the meantime, questions? Reply directly to any email from us, or reach out at <a href="mailto:info@prsvr.com" className="text-white hover:opacity-80 underline">info@prsvr.com</a>.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="apply-interest-form">
            <Field label="Parent Name" required value={form.parent_name} onChange={on('parent_name')} placeholder="Alexandra Sovereign" testid="apply-parent-name" />
            <Field label="Parent Email" required type="email" value={form.parent_email} onChange={on('parent_email')} placeholder="parent@example.com" testid="apply-parent-email" />
            <Field label="Student Name" required value={form.student_name} onChange={on('student_name')} placeholder="Marcus Sovereign" testid="apply-student-name" />
            <div>
              <label className="block text-[10px] font-black tracking-[0.28em] uppercase mb-2" style={{ color: GOLD }}>
                Student Grade for 2026&ndash;27 <span className="text-red-400">*</span>
              </label>
              <select
                required
                value={form.student_grade}
                onChange={on('student_grade')}
                className="w-full border border-white/15 focus:border-white/50 px-4 py-3.5 text-white text-sm font-bold tracking-wide transition-colors outline-none"
                style={{ backgroundColor: '#0A0A0E', colorScheme: 'dark' }}
                data-testid="apply-student-grade"
              >
                <option value="" className="bg-[#0A0A0E]">Select a grade</option>
                {['TK', 'K', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th'].map((g) => (
                  <option key={g} value={g} className="bg-[#0A0A0E]">{g}</option>
                ))}
              </select>
            </div>

            {error && (
              <div className="text-red-400 text-sm font-bold tracking-wide" data-testid="apply-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 text-[11px] font-black tracking-[0.22em] uppercase text-black px-6 py-4 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              style={{ backgroundColor: ORANGE, boxShadow: `0 0 0 1px ${ORANGE}, 0 10px 24px -10px ${ORANGE}90` }}
              data-testid="apply-interest-submit"
            >
              {submitting ? 'Reserving your spot...' : 'Notify Me When Enrollment Opens'}
              <ChevronRight className="h-4 w-4" />
            </button>

            <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-600 text-center pt-2">
              We&apos;ll never share your info · One email when the portal opens
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, required, value, onChange, placeholder, type = 'text', testid }) => (
  <div>
    <label className="block text-[10px] font-black tracking-[0.28em] uppercase mb-2" style={{ color: GOLD }}>
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full border border-white/15 focus:border-white/50 px-4 py-3.5 text-white text-sm font-bold tracking-wide transition-colors outline-none placeholder:text-gray-600 placeholder:font-normal"
      style={{ backgroundColor: '#0A0A0E', colorScheme: 'dark' }}
      data-testid={testid}
    />
  </div>
);

export default AcademyApplyComingSoon;
